import { LegalLayout } from '@/components/LegalLayout';
import { useLanguage } from '@/lib/i18n/LanguageContext';

export default function PrivacyPage() {
  const { lang } = useLanguage();

  if (lang === 'tr') return (
    <LegalLayout title="Gizlilik Politikası" lastUpdated="24 Şubat 2026">
      <p>Safe Crypto ("Şirket", "biz") olarak kişisel verilerinizin korunmasına büyük önem veriyoruz. Bu Gizlilik Politikası, safecrypto.ai platformunu kullanırken toplanan, işlenen ve saklanan kişisel verileriniz hakkında sizi bilgilendirmek amacıyla hazırlanmıştır.</p>

      <h2>1. Toplanan Veriler</h2>
      <p>Platformumuzu kullanırken aşağıdaki kişisel verileriniz toplanabilir:</p>
      <ul>
        <li>Kimlik bilgileri: Ad, soyad, doğum tarihi, T.C. kimlik numarası (KYC sürecinde)</li>
        <li>İletişim bilgileri: E-posta adresi, telefon numarası</li>
        <li>Finansal bilgiler: Cüzdan adresleri, işlem geçmişi, bakiye bilgileri</li>
        <li>Teknik bilgiler: IP adresi, tarayıcı bilgisi, cihaz bilgileri, çerezler</li>
        <li>Kimlik doğrulama belgeleri: Nüfus cüzdanı/pasaport fotoğrafı, selfie</li>
      </ul>

      <h2>2. Verilerin Kullanım Amaçları</h2>
      <ul>
        <li>Hesap oluşturma ve kimlik doğrulama</li>
        <li>Kripto para alım-satım ve transfer işlemlerinin gerçekleştirilmesi</li>
        <li>Yasal yükümlülüklerin yerine getirilmesi (AML/KYC)</li>
        <li>Dolandırıcılık ve kara para aklamanın önlenmesi</li>
        <li>Müşteri desteği sağlanması</li>
        <li>Platform güvenliğinin sağlanması</li>
        <li>İstatistiksel analiz ve hizmet iyileştirme</li>
      </ul>

      <h2>3. Verilerin Paylaşımı</h2>
      <p>Kişisel verileriniz, aşağıdaki durumlar dışında üçüncü taraflarla paylaşılmaz:</p>
      <ul>
        <li>Yasal zorunluluklar (mahkeme kararları, düzenleyici kurum talepleri)</li>
        <li>Ödeme işlemcileri ve blockchain ağları (işlem onayı için)</li>
        <li>KYC/AML hizmet sağlayıcıları (kimlik doğrulama için)</li>
        <li>Güvenlik ve dolandırıcılık önleme hizmetleri</li>
      </ul>

      <h2>4. Veri Güvenliği</h2>
      <p>Verilerinizi korumak için aşağıdaki önlemleri uyguluyoruz:</p>
      <ul>
        <li>256-bit SSL/TLS şifreleme</li>
        <li>İki faktörlü kimlik doğrulama (2FA)</li>
        <li>Soğuk cüzdan depolama (cold wallet storage)</li>
        <li>Düzenli güvenlik denetimleri ve penetrasyon testleri</li>
        <li>Veri erişim kontrolleri ve loglanması</li>
      </ul>

      <h2>5. Veri Saklama Süresi</h2>
      <p>Kişisel verileriniz, yasal saklama süreleri ve işleme amaçları sona erdikten sonra güvenli bir şekilde silinir veya anonim hale getirilir. Finansal işlem kayıtları yasal zorunluluklar gereği en az 10 yıl saklanır.</p>

      <h2>6. Haklarınız</h2>
      <p>KVKK ve GDPR kapsamında aşağıdaki haklara sahipsiniz:</p>
      <ul>
        <li>Verilerinize erişim hakkı</li>
        <li>Düzeltme ve güncelleme hakkı</li>
        <li>Silme hakkı (yasal yükümlülükler saklı kalmak kaydıyla)</li>
        <li>İşlemenin kısıtlanmasını talep etme hakkı</li>
        <li>Veri taşınabilirliği hakkı</li>
        <li>İtiraz hakkı</li>
      </ul>

      <h2>7. İletişim</h2>
      <p>Gizlilik politikamız hakkında sorularınız için <strong>support@safecrypto.ai</strong> adresinden bize ulaşabilirsiniz.</p>
    </LegalLayout>
  );

  return (
    <LegalLayout title="Privacy Policy" lastUpdated="February 24, 2026">
      <p>Safe Crypto ("Company", "we") places great importance on the protection of your personal data. This Privacy Policy has been prepared to inform you about the personal data collected, processed and stored while using the safecrypto.ai platform.</p>

      <h2>1. Data Collected</h2>
      <p>The following personal data may be collected when using our platform:</p>
      <ul>
        <li>Identity information: Name, surname, date of birth, national ID (during KYC)</li>
        <li>Contact information: Email address, phone number</li>
        <li>Financial information: Wallet addresses, transaction history, balance information</li>
        <li>Technical information: IP address, browser info, device information, cookies</li>
        <li>Identity verification documents: ID/passport photo, selfie</li>
      </ul>

      <h2>2. Purposes of Data Use</h2>
      <ul>
        <li>Account creation and identity verification</li>
        <li>Execution of cryptocurrency buy/sell and transfer transactions</li>
        <li>Fulfillment of legal obligations (AML/KYC)</li>
        <li>Prevention of fraud and money laundering</li>
        <li>Providing customer support</li>
        <li>Ensuring platform security</li>
        <li>Statistical analysis and service improvement</li>
      </ul>

      <h2>3. Data Sharing</h2>
      <p>Your personal data will not be shared with third parties except in the following cases:</p>
      <ul>
        <li>Legal obligations (court orders, regulatory body requests)</li>
        <li>Payment processors and blockchain networks (for transaction confirmation)</li>
        <li>KYC/AML service providers (for identity verification)</li>
        <li>Security and fraud prevention services</li>
      </ul>

      <h2>4. Data Security</h2>
      <ul>
        <li>256-bit SSL/TLS encryption</li>
        <li>Two-factor authentication (2FA)</li>
        <li>Cold wallet storage</li>
        <li>Regular security audits and penetration tests</li>
        <li>Data access controls and logging</li>
      </ul>

      <h2>5. Data Retention</h2>
      <p>Your personal data is securely deleted or anonymized after legal retention periods and processing purposes expire. Financial transaction records are retained for at least 10 years as required by law.</p>

      <h2>6. Your Rights</h2>
      <ul>
        <li>Right to access your data</li>
        <li>Right to correction and update</li>
        <li>Right to deletion (subject to legal obligations)</li>
        <li>Right to restrict processing</li>
        <li>Right to data portability</li>
        <li>Right to object</li>
      </ul>

      <h2>7. Contact</h2>
      <p>For questions about our privacy policy, contact us at <strong>support@safecrypto.ai</strong>.</p>
    </LegalLayout>
  );
}
